// utils/liff.ts
import type { lineData } from '@/interfaces/auth'
import { useValidateLoginState } from '@/api/useLogin'
import { useAuthStore } from '@/stores/auth'
import liff from '@line/liff'
import type { App } from 'vue'
import type { Router } from 'vue-router'

let _router: Router

export const setRouter = (router: Router) => {
  _router = router
}

// ─────────────────────────────────────────
// Init LIFF
// ─────────────────────────────────────────
export const initLiff = () => {
  const auth = useAuthStore()

  // ✅ อ่าน params ก่อนเลย ก่อน liff init
  const params = new URLSearchParams(window.location.search)
  const paymentStatus = params.get('paymentStatus')
  const orderId = params.get('orderId')
  const txn = params.get('txn')

  // ✅ ถ้ามี paymentStatus → จัดการก่อนเลย ไม่ต้องรอ liff init
  if (paymentStatus) {
    handlePaymentResult({ paymentStatus, orderId, txn })
    return Promise.resolve()
  }

  return liff
    .init({
      liffId: import.meta.env.VITE_MJSB2B_LINE_LIFF_CHANNEL_ID,
      withLoginOnExternalBrowser: false,
    })
    .then(async () => {
      if (liff.isLoggedIn()) {
        const access_line_token = liff.getAccessToken() as string
        const profile = await getUserProfile()
        auth.loginWithLiff(access_line_token, profile)
      } else {
        // liff.login()
      }
    })
    .catch((err) => {
      console.log('🔴 liff init error:', err)
    })
}

// ─────────────────────────────────────────
// Validate login state against the server, using the LINE uid
// Replaces trusting a cached localStorage token: on app startup we ask the
// backend whether this LINE uid still has a valid session.
//   200 + data → same login() call the OTP-confirm step uses, so the user
//                continues straight into the app.
//   404 / any failure → ensure a clean logged-out state so the auth guard
//                falls back to /login.
// Must run after initLiff() has resolved (needs auth.liffProfile.userId),
// and must be invoked within Vue's app context (see main.ts's
// app.runWithContext) since useValidateLoginState() relies on inject().
// ─────────────────────────────────────────
export const validateLoginSession = async () => {
  const auth = useAuthStore()
  const lineId = auth.liffProfile?.userId

  if (!lineId) {
    auth.logout()
    return
  }

  const { mutateAsync } = useValidateLoginState()

  try {
    const memberData = await mutateAsync({ lineId })
    auth.login(memberData.key ?? '', memberData)
  } catch (err) {
    console.log('🔴 validateLoginSession error:', err)
    auth.logout()
  }
}

// ─────────────────────────────────────────
// Auth bootstrap coordination
// Vue Router kicks off its initial navigation as soon as `app.use(router)`
// runs (see vue-router's install()), NOT when app.mount() runs — so ordering
// initLiff()/validateLoginSession() before app.mount() in main.ts does NOT
// delay that first navigation. The guard (src/core/guard.ts) must instead
// await this shared promise itself before deciding whether a route is
// authenticated, so it always sees the server-validated state rather than
// whatever auth.token happened to be at the moment the router started.
// ─────────────────────────────────────────
let authBootstrapPromise: Promise<void> = Promise.resolve()

export const bootstrapAuth = (app: App) => {
  authBootstrapPromise = (async () => {
    await initLiff()
    await app.runWithContext(() => validateLoginSession())
  })()

  return authBootstrapPromise
}

export const waitForAuthBootstrap = () => authBootstrapPromise

// ─────────────────────────────────────────
// Get User Profile
// ─────────────────────────────────────────
export const getUserProfile = async () => {
  const profile = await liff.getProfile()
  const accessToken = liff.getIDToken()
  const friendShip = await liff.getFriendship()

  return {
    pictureUrl: profile.pictureUrl,
    userId: profile.userId,
    statusMessage: profile.statusMessage,
    displayName: profile.displayName,
    email: null,
    accessToken: accessToken,
    isFriendship: friendShip.friendFlag,
  } as lineData
}

// ─────────────────────────────────────────
// Redirect ไป LIFF (เปิดใน LINE App)
// ─────────────────────────────────────────
export const redirectToLiff = (params?: Record<string, string>) => {
  const liffId = import.meta.env.VITE_MJSB2B_LINE_LIFF_CHANNEL_ID

  const query = params ? '?' + new URLSearchParams(params).toString() : ''

  window.location.href = `https://liff.line.me/${liffId}${query}`
}

// ─────────────────────────────────────────
// Handle Payment Result
// ทำงานก่อน liff init เพราะไม่ต้องการ login
// ─────────────────────────────────────────
const handlePaymentResult = (params: {
  paymentStatus: string | null
  orderId: string | null
  txn: string | null
}) => {
  const { paymentStatus, orderId, txn } = params
  if (!paymentStatus) return

  console.log('🟡 handlePaymentResult:', { paymentStatus, orderId, txn })

  window.history.replaceState({}, '', window.location.pathname)

  if (paymentStatus === 'success') {
    _router.push({
      path: '/payment/success',
      query: { orderId: orderId ?? '', txn: txn ?? '' },
    })
  } else {
    _router.push({
      path: '/payment/failed',
      query: { orderId: orderId ?? '' },
    })
  }
}
